import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { Text } = Typography;

interface DeviceRow {
	device_id: number;
	phone_model: string;
	customer_name: string;
	customer_phone: string;
	contract_date: string;
	total_payable: number;
	paid: number;
	balance: number;
	overdue_installments_count: number;
	overdue_amount: number;
	longest_overdue_days: number;
	is_completed: boolean;
	is_recovered: boolean;
}

interface DevicesResponse {
	devices: DeviceRow[];
	count: number;
	page: number;
	limit: number;
}

const ugx = (v: number) => `UGX ${formatNumber(v)}`;

const filterLabel: Record<string, string> = {
	sold: 'All Sold',
	completed: 'Completed',
	recovered: 'Recovered',
	pending: 'Pending',
};

const SalesReportDevices = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const retailerId = searchParams.get('retailer_id') ?? '';
	const retailerName = searchParams.get('retailer_name') ?? 'Retailer';
	const filter = searchParams.get('filter') ?? 'sold';
	const start = searchParams.get('start') ?? dayjs().startOf('year').format('YYYY-MM-DD');
	const end = searchParams.get('end') ?? dayjs().format('YYYY-MM-DD');

	const [pagination, setPagination] = useState({ page: 1, pageSize: 20 });

	const { data, isLoading, error } = useQuery({
		queryKey: ['sales-report-devices', retailerId, filter, start, end, pagination],
		queryFn: async () => {
			const response = await axios.get('/admin/reports/sales/devices', {
				withCredentials: true,
				params: {
					retailer_id: retailerId,
					filter,
					start,
					end,
					page: pagination.page,
					limit: pagination.pageSize,
				},
			});
			return response.data.results as DevicesResponse;
		},
		enabled: !!retailerId,
	});

	if (error) handleAPIError(error);

	const rows = data?.devices ?? [];

	const handleExport = () => {
		if (!rows.length) return;
		const sheet = XLSX.utils.json_to_sheet(
			rows.map((r) => ({
				'Device ID': r.device_id,
				'Phone Model': r.phone_model,
				'Customer Name': r.customer_name,
				'Customer Phone': r.customer_phone,
				'Contract Date': r.contract_date,
				'Total Payable (UGX)': r.total_payable,
				'Paid (UGX)': r.paid,
				'Balance (UGX)': r.balance,
				'Overdue Installments': r.overdue_installments_count,
				'Overdue Amount (UGX)': r.overdue_amount,
				'Longest Overdue (days)': r.longest_overdue_days,
				'Status': r.is_completed ? 'Completed' : r.is_recovered ? 'Recovered' : 'Pending',
			}))
		);
		sheet['!cols'] = [
			{ wch: 10 }, { wch: 20 }, { wch: 22 }, { wch: 16 }, { wch: 14 },
			{ wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 12 },
		];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, sheet, 'Devices');
		XLSX.writeFile(wb, `${retailerName}-${filterLabel[filter]}-${start}-${end}.xlsx`);
	};

	const columns: ColumnsType<DeviceRow> = [
		{
			title: 'Customer',
			key: 'customer',
			width: 200,
			render: (_, r) => (
				<div>
					<div className="font-medium">{r.customer_name}</div>
					<div className="text-xs text-gray-500">{r.customer_phone}</div>
				</div>
			),
		},
		{
			title: 'Phone Model',
			dataIndex: 'phone_model',
			key: 'phone_model',
			width: 160,
		},
		{
			title: 'Contract Date',
			dataIndex: 'contract_date',
			key: 'contract_date',
			width: 130,
			render: (v: string) => dayjs(v).format('DD MMM YYYY'),
			sorter: (a, b) => a.contract_date.localeCompare(b.contract_date),
		},
		{
			title: 'Status',
			key: 'status',
			width: 110,
			render: (_, r) =>
				r.is_completed ? (
					<Tag color="green">Completed</Tag>
				) : r.is_recovered ? (
					<Tag color="default">Recovered</Tag>
				) : (
					<Tag color="orange">Pending</Tag>
				),
			filters: [
				{ text: 'Completed', value: 'completed' },
				{ text: 'Recovered', value: 'recovered' },
				{ text: 'Pending', value: 'pending' },
			],
			onFilter: (value, r) =>
				value === 'completed' ? r.is_completed :
				value === 'recovered' ? r.is_recovered && !r.is_completed :
				!r.is_completed && !r.is_recovered,
		},
		{
			title: 'Total Payable',
			dataIndex: 'total_payable',
			key: 'total_payable',
			width: 160,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_payable - b.total_payable,
		},
		{
			title: 'Paid',
			dataIndex: 'paid',
			key: 'paid',
			width: 150,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.paid - b.paid,
		},
		{
			title: 'Balance',
			dataIndex: 'balance',
			key: 'balance',
			width: 150,
			align: 'right',
			sorter: (a, b) => a.balance - b.balance,
			render: (v: number) => {
				if (v < 0) return <Tag color="orange">UGX {formatNumber(Math.abs(v))} overpaid</Tag>;
				return ugx(v);
			},
		},
		{
			title: 'Overdue Installments',
			dataIndex: 'overdue_installments_count',
			key: 'overdue_installments_count',
			width: 160,
			align: 'center',
			sorter: (a, b) => a.overdue_installments_count - b.overdue_installments_count,
			render: (v: number) =>
				v > 0 ? <Tag color="red">{v}</Tag> : <Text style={{ color: '#52c41a' }}>—</Text>,
		},
		{
			title: 'Overdue Amount',
			dataIndex: 'overdue_amount',
			key: 'overdue_amount',
			width: 160,
			align: 'right',
			sorter: (a, b) => a.overdue_amount - b.overdue_amount,
			defaultSortOrder: 'descend',
			render: (v: number) =>
				v > 0 ? (
					<Text strong style={{ color: '#ff4d4f' }}>
						UGX {formatNumber(v)}
					</Text>
				) : (
					<Text style={{ color: '#52c41a' }}>—</Text>
				),
		},
		{
			title: 'Longest Overdue',
			dataIndex: 'longest_overdue_days',
			key: 'longest_overdue_days',
			width: 140,
			align: 'center',
			sorter: (a, b) => a.longest_overdue_days - b.longest_overdue_days,
			render: (v: number) =>
				v > 0 ? (
					<Text style={{ color: v > 30 ? '#ff4d4f' : '#faad14' }}>{v} days</Text>
				) : (
					<Text style={{ color: '#52c41a' }}>—</Text>
				),
		},
	];

	return (
		<AppLayout
			title={`${retailerName} — ${filterLabel[filter]} Devices`}
			subtitle={`${start} to ${end}`}
		>
			<div className="mb-4 flex items-center justify-between">
				<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
					Back to Sales Report
				</Button>
				<Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!rows.length}>
					Export
				</Button>
			</div>

			<Card>
				<Table
					columns={columns}
					dataSource={rows}
					loading={isLoading}
					rowKey="device_id"
					scroll={{ x: 1400 }}
					pagination={{
						current: pagination.page,
						pageSize: pagination.pageSize,
						total: data?.count ?? 0,
						onChange: (page, pageSize) => setPagination({ page, pageSize: pageSize ?? 20 }),
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} devices`,
					}}
				/>
			</Card>
		</AppLayout>
	);
};

export default SalesReportDevices;
